import React, { createContext, useContext, useMemo } from 'react';
import { useFirebase } from './FirebaseContext';
import { useAuth } from './AuthContext';
import ErrorLoggingService from '../services/errorLoggingService';

const ErrorContext = createContext();

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

export const ErrorProvider = ({ children }) => {
  const { analytics, db } = useFirebase();
  const { currentUser } = useAuth();

  // Create error logging service instance
  const errorLoggingService = useMemo(() => {
    return new ErrorLoggingService(analytics, db);
  }, [analytics, db]);

  // Process pending errors from global handlers
  React.useEffect(() => {
    if (window.__PENDING_ERRORS__ && errorLoggingService && analytics) {
      const pendingErrors = window.__PENDING_ERRORS__;
      window.__PENDING_ERRORS__ = []; // Atomically clear the array
      
      pendingErrors.forEach(async (pendingError) => {
        try {
          if (pendingError.type === 'javascript_error') {
            await errorLoggingService.logJavaScriptError(
              pendingError.message,
              pendingError.filename,
              pendingError.lineno,
              pendingError.colno,
              pendingError.error,
              currentUser
            );
          } else if (pendingError.type === 'promise_rejection') {
            await errorLoggingService.logPromiseRejection(
              pendingError.reason,
              pendingError.promise,
              currentUser
            );
          }
        } catch (logError) {
          console.error('Failed to process pending error:', logError);
        }
      });
    }
  }, [errorLoggingService, analytics, currentUser]);

  // Error logging methods
  const logJavaScriptError = async (message, filename, lineno, colno, error) => {
    await errorLoggingService.logJavaScriptError(message, filename, lineno, colno, error, currentUser);
  };

  const logPromiseRejection = async (reason, promise) => {
    await errorLoggingService.logPromiseRejection(reason, promise, currentUser);
  };

  const logReactError = async (error, errorInfo) => {
    await errorLoggingService.logReactError(error, errorInfo, currentUser);
  };

  const logCustomError = async (errorType, error, additionalData = {}) => {
    await errorLoggingService.logCustomError(errorType, error, currentUser, additionalData);
  };

  const logNetworkError = async (error, requestDetails = {}) => {
    // Prevent infinite loops when auth/network errors trigger more network calls
    if (error?.message?.includes('auth/quota-exceeded') || 
        error?.message?.includes('Firebase: Error (auth/quota-exceeded)') ||
        requestDetails?.endpoint?.includes('/api/')) {
      console.error('Network error (not logged to prevent loops):', error?.message);
      return;
    }
    
    await errorLoggingService.logCustomError('network_error', error, currentUser, {
      ...requestDetails,
      critical: true // Network errors are typically critical
    });
  };

  const logAuthenticationError = async (error, authAction = '') => {
    // Prevent infinite loops when auth errors trigger more auth calls
    if (error?.message?.includes('auth/quota-exceeded') || 
        error?.message?.includes('Firebase: Error (auth/quota-exceeded)')) {
      console.error('Auth error (not logged to prevent loops):', error?.message);
      return;
    }
    
    await errorLoggingService.logCustomError('authentication_error', error, currentUser, {
      auth_action: authAction,
      critical: true
    });
  };

  const logFirebaseError = async (error, operation = '') => {
    // Prevent infinite loops when Firebase errors trigger more Firebase calls
    if (error?.message?.includes('auth/quota-exceeded') || 
        error?.message?.includes('Firebase: Error (auth/quota-exceeded)')) {
      console.error('Firebase error (not logged to prevent loops):', error?.message);
      return;
    }
    
    await errorLoggingService.logCustomError('firebase_error', error, currentUser, {
      firebase_operation: operation,
      critical: true
    });
  };

  const logValidationError = async (error, formField = '', formData = {}) => {
    await errorLoggingService.logCustomError('validation_error', error, currentUser, {
      form_field: formField,
      form_data: JSON.stringify(formData).substring(0, 500), // Truncate large form data
      critical: false
    });
  };

  const logPerformanceIssue = async (metricName, value) => {
    await errorLoggingService.logPerformanceIssue(metricName, value, currentUser);
  };

  // Helper method for safely executing async operations with error logging
  const withErrorLogging = (asyncFn, errorType = 'async_operation_error') => {
    return async (...args) => {
      try {
        return await asyncFn(...args);
      } catch (error) {
        await logCustomError(errorType, error, {
          function_name: asyncFn.name,
          arguments: JSON.stringify(args).substring(0, 200)
        });
        throw error; // Re-throw to maintain original behaviour
      }
    };
  };

  // Helper method for wrapping components with error logging
  const withComponentErrorLogging = (Component, componentName) => {
    return React.forwardRef((props, ref) => {
      try {
        return <Component {...props} ref={ref} />;
      } catch (error) {
        logCustomError('component_render_error', error, {
          component_name: componentName,
          props: JSON.stringify(props).substring(0, 500)
        });
        throw error; // Let Error Boundary handle it
      }
    });
  };

  const value = {
    // Core logging methods
    logJavaScriptError,
    logPromiseRejection,
    logReactError,
    logCustomError,
    
    // Specific error type methods
    logNetworkError,
    logAuthenticationError,
    logFirebaseError,
    logValidationError,
    logPerformanceIssue,
    
    // Helper methods
    withErrorLogging,
    withComponentErrorLogging,
    
    // Access to underlying service if needed
    errorLoggingService
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};